import React, {ReactNode} from 'react';
import {Platform, StyleProp, View, ViewStyle} from 'react-native';
import {noop} from 'lodash';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import {generateCallback} from '/util/component.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './ArrangedIcons.style';

type IconName = string;

interface IconStyles {
  base?: StyleProp<ViewStyle>;
  icon?: StyleProp<ViewStyle>;
  iconLeft?: StyleProp<ViewStyle>;
  iconRight?: StyleProp<ViewStyle>;
  iconsGap?: StyleProp<ViewStyle>;
  iconsRight?: StyleProp<ViewStyle>;
}

interface ArrangedIconsProps {
  leftIcons?: IconName[];
  rightIcons?: IconName[];
  onIconPress?: (iconName: IconName) => void;
  style?: IconStyles;
  children?: ReactNode;
  accessibilityLabelsLeft?: string[];
  accessibilityLabelsRight?: string[];
  leftIconTestIDs?: (string | null)[];
  rightIconTestIDs?: (string | null)[];
}

export class ArrangedIcons extends React.PureComponent<ArrangedIconsProps> {
  static defaultProps: Partial<ArrangedIconsProps> = {
    leftIcons: [],
    rightIcons: [],
    onIconPress: noop,
    style: {},
    children: null,
    accessibilityLabelsLeft: [],
    accessibilityLabelsRight: [],
    leftIconTestIDs: [],
    rightIconTestIDs: [],
  };

  handleIconPress = (iconName: IconName) => {
    return generateCallback({
      func: this.props.onIconPress,
      params: iconName,
    });
  };

  render() {
    const {
      leftIcons = [],
      rightIcons = [],
      style: styleProp = {},
      children = null,
      accessibilityLabelsLeft = [],
      accessibilityLabelsRight = [],
      leftIconTestIDs = [],
      rightIconTestIDs = [],
    } = this.props;

    const containerStyle = [styles.base, styleProp.base];

    const rightIconsWithNoSearchNotification = rightIcons.filter(
      (value: any) => value !== 'notifications',
    );

    return (
      <View style={containerStyle}>
        <View style={styles.iconsLeft}>
          {leftIcons.map((iconName: any, index: any) => {
            const accessibilityLabel =
              accessibilityLabelsLeft && accessibilityLabelsLeft[index]
                ? `${accessibilityLabelsLeft[index]} button`
                : iconName === 'chevron-left'
                ? 'Back button'
                : `${iconName} button`;
            return (
              <KeyboardFocusable
                // @ts-ignore: iOS keyboard focus
                focusable={true}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel={accessibilityLabel}
                {...testProperties(leftIconTestIDs[index] || null, true)}
                key={`${index}-${iconName}`}
                onPress={this.handleIconPress(iconName)}>
                <Icon
                  accessible={Platform.OS === 'ios'}
                  accessibilityLabel={accessibilityLabel}
                  name={iconName}
                  style={{
                    ...styles.icon,
                    ...(styleProp.icon || {}),
                    ...styles.iconLeft,
                    ...(styleProp.iconLeft || {}),
                  }}
                />
              </KeyboardFocusable>
            );
          })}
        </View>

        <View style={{...styles.iconsGap, ...(styleProp.iconsGap || {})}}>
          {children}
        </View>
        <View
          style={{...styles.iconsRight, ...styleProp.iconsRight}}
          accessible={false}>
          {rightIconsWithNoSearchNotification.map(
            (iconName: any, index: any) => {
              const accessibilityLabel =
                accessibilityLabelsRight && accessibilityLabelsRight[index]
                  ? `${accessibilityLabelsRight[index]} button`
                  : iconName === 'search'
                  ? 'Search button'
                  : `${iconName} button`;
              return (
                <KeyboardFocusable
                  // @ts-ignore: iOS keyboard focus
                  focusable={true}
                  key={`${index}-${iconName}`}
                  accessible={true}
                  accessibilityRole="button"
                  accessibilityLabel={accessibilityLabel}
                  onPress={this.handleIconPress(iconName)}
                  {...testProperties(rightIconTestIDs[index] || null, true)}>
                  <Icon
                    accessible={false}
                    name={iconName}
                    style={{
                      ...styles.icon,
                      ...(styleProp.icon || {}),
                      ...styles.iconRight,
                      ...(styleProp.iconRight || {}),
                      ...(iconName === 'message' ? styles.chatIconRight : {}),
                    }}
                  />
                </KeyboardFocusable>
              );
            },
          )}
        </View>
      </View>
    );
  }
}

export default ArrangedIcons;
